import { setCssVar, useMeta } from 'quasar'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { type RouteLocationRaw, useRoute, useRouter } from 'vue-router'

import { useApiAuth } from '@/composables/api/auth'
import { useApiClient } from '@/composables/api/client'
import { useTransformField } from '@/composables/transformField'
import { useNotifications } from '@/composables/useNotifications'
import themeConfig from '@/config/theme.json'
import { useAppStore } from '@/store/modules/app'
import { useAuthStore } from '@/store/modules/auth'
import type { PageSearchProps } from '@/types/page'
import { dayjs } from '@/utils/time'

const excludeFromErrorHandlingUrls = {
  'listings/map': [400],
  'listings/search': [400],
  locations: [400],
}

export const useBoot = () => {
  const route = useRoute()
  const router = useRouter()
  const appStore = useAppStore()
  const authStore = useAuthStore()

  const { csrfToken } = useApiAuth()
  const { apiClient, error } = useApiClient()
  const { getValuesFromQuasarOptions } = useTransformField()
  const { notifyHttpConflict, notifyDeleteUser } = useNotifications()

  const { locale } = useI18n()

  const link = ref<Record<string, { href: string; rel: string }>>({})
  const { typography, components } = themeConfig

  const applyAxiosInterceptors = () => {
    apiClient.interceptors.request.use((_config) => {
      const config = { ..._config }

      config.headers['Accept-Language'] = locale.value
      config.headers['Accept-Timezone'] = dayjs.tz.guess()

      return config
    })

    apiClient.interceptors.response.use(
      (res) => res,
      async (err) => {
        const status = Number(err?.response?.status || '500')

        const url = err.config.url as keyof typeof excludeFromErrorHandlingUrls

        const isExcluded = excludeFromErrorHandlingUrls[url] ?? []

        switch (status) {
          case 400:
          case 404:
          case 500:
            if (isExcluded.includes(status)) {
              break
            }

            error.value = true
            router.replace({ name: 'error', params: { status } })

            break

          case 401:
            authStore.user = null

            break

          case 409:
            notifyHttpConflict(err?.response?.data?.message)

            break

          case 419:
            await csrfToken()

            return apiClient.request(err.config)

          default:
            break
        }

        return Promise.reject(err)
      },
    )
  }

  const applyTypography = () => {
    if (typography.fontFamilyCdnUrl) {
      link.value.fontFamilyCdn = {
        href: typography.fontFamilyCdnUrl,
        rel: 'stylesheet',
      }
    }

    document.documentElement.style.setProperty(
      '--font-family',
      typography.fontFamily,
    )
  }

  const applyCss = () => {
    const listItemCss = appStore.configTheme?.list_item.css

    if (!listItemCss) {
      setTimeout(() => {
        applyCss()
      }, 100)

      return
    }

    Promise.all([
      import(`@/css/navbar/${components.AppNavBar.cssUrl}/index.scss`),
      import(`@/css/listing/${listItemCss}/index.scss`),
    ])
      .then(([navBarModule, spListItemModule]) => {
        const navBarStyle = document.createElement('style')
        navBarStyle.innerHTML = navBarModule.default
        document.head.appendChild(navBarStyle)

        const spListItemStyle = document.createElement('style')
        spListItemStyle.innerHTML = spListItemModule.default
        document.head.appendChild(spListItemStyle)
      })
      .catch((e) => {
        console.error('Error loading CSS:', e)
      })
  }

  const searchPageValidateProps = (props: PageSearchProps) => {
    const validSortingOptionValues = getValuesFromQuasarOptions(
      appStore.optionsSortingOptions,
    )

    const { maxZoom, minZoom, zoom } =
      appStore.configTheme!.page.search.map.options

    let triggerRedirection: boolean = false

    const routeRedirect: RouteLocationRaw = {
      name: 'search',
      params: {
        locale: props.locale,
      },
      query: {},
    }

    if (!appStore.optionsCategories.includes(props.category)) {
      routeRedirect.params = {
        ...routeRedirect.params,
        category: appStore.defaultCategory || '',
      }

      console.log('invalid category: ', props.category)

      triggerRedirection = true
    }

    if (!appStore.optionsCommercializations.includes(props.commercialization)) {
      routeRedirect.params = {
        ...routeRedirect.params,
        commercialization: appStore.defaultCommercialization || '',
      }

      console.log('invalid commercialization: ', props.commercialization)

      triggerRedirection = true
    }

    const routeQuerySortBy = route.query.sort_by?.toString() || ''

    if (!validSortingOptionValues?.includes(routeQuerySortBy)) {
      routeRedirect.query = {
        ...routeRedirect.query,
        sort_by: appStore.defaultSortingOption.value,
      }

      console.log('invalid sort by: ', routeQuerySortBy)

      triggerRedirection = true
    }

    const routeQueryZoom = Number(route.query.zoom?.toString())

    if (
      Number.isNaN(routeQueryZoom)
      || routeQueryZoom < minZoom
      || routeQueryZoom > maxZoom
    ) {
      routeRedirect.query = {
        ...routeRedirect.query,
        zoom,
      }

      console.log('invalid zoom: ', routeQueryZoom)

      triggerRedirection = true
    }

    if (triggerRedirection) {
      console.warn('i am triggered', {
        name: 'search',
        params: {
          ...route.params,
          ...routeRedirect.params,
        },
        query: {
          ...route.query,
          ...routeRedirect.query,
        },
      })

      router.replace({
        name: 'search',
        params: {
          ...route.params,
          ...routeRedirect.params,
        },
        query: {
          ...route.query,
          ...routeRedirect.query,
        },
      })
    }
  }

  const applyCssVars = () => {
    if (!appStore.configTheme) {
      setTimeout(() => {
        applyCssVars()
      }, 100)

      return
    }

    const { color, radius } = appStore.configTheme

    Object.keys(color).forEach((k) => {
      setCssVar(k, color[k])
    })

    Object.keys(radius).forEach((k) => {
      setCssVar(`radius-${k}`, radius[k])
    })
  }

  const bootApp = () => {
    applyTypography()

    applyAxiosInterceptors()

    applyCssVars()

    applyCss()

    useMeta({ link: link.value })

    notifyDeleteUser()
  }

  const bootSearch = (props: PageSearchProps) => {
    searchPageValidateProps(props)

    const css = appStore.configTheme?.page.search.css

    if (!css) {
      return
    }

    import(`@/css/page/search/${css}/index.scss`)
      .then((module) => {
        const style = document.createElement('style')

        style.innerHTML = module.default

        document.head.appendChild(style)
      })
      .catch((e) => {
        console.error('Error loading CSS:', e)
      })
  }

  return { bootApp, bootSearch }
}
